import { useCallback } from 'react';

import { useKeyDown } from '../../hooks/useKeyDown';
import { newGame, NumberOfLetters } from '../../reducers/game';
import { regionSelector } from '../../selectors/settings';
import { useAppDispatch, useAppSelector } from '../../store';
import { KeyboardHint } from '../keyboard-hint/KeyboardHint';

import { WordLengthPickerButton } from './WordLengthPickerButton';
import { WordLengthPickerWrapper } from './WordLengthPickerWrapper';

type WordLength = {
  backgroundColor: string;
  numberOfLetters: NumberOfLetters;
};

const LENGTHS: WordLength[] = [
  { backgroundColor: '#4d908e', numberOfLetters: NumberOfLetters.THREE },
  { backgroundColor: '#43aa8b', numberOfLetters: NumberOfLetters.FOUR },
  { backgroundColor: '#90be6d', numberOfLetters: NumberOfLetters.FIVE },
  { backgroundColor: '#f9c74f', numberOfLetters: NumberOfLetters.SIX },
  { backgroundColor: '#f8961e', numberOfLetters: NumberOfLetters.SEVEN },
  { backgroundColor: '#f3722c', numberOfLetters: NumberOfLetters.EIGHT },
];

/* Defined out here so the key listener is not torn down and rebuilt every render. */
const LENGTH_KEYS = LENGTHS.map(({ numberOfLetters }) => String(numberOfLetters));

export const WordLengthPicker = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const region = useAppSelector(regionSelector);

  const startGame = useCallback(
    (numberOfLetters: NumberOfLetters) => {
      dispatch(newGame({ numberOfLetters, region }));
    },
    [dispatch, region],
  );

  /* Typing a length is quicker than reaching for the mouse, and the digits are
   * already what the buttons are labelled with. */
  const onKey = useCallback(
    (key: string) => {
      startGame(Number(key) as NumberOfLetters);
    },
    [startGame],
  );

  useKeyDown(LENGTH_KEYS, onKey);

  return (
    <>
      <WordLengthPickerWrapper>
        {LENGTHS.map(({ backgroundColor, numberOfLetters }) => {
          const onClick = (): void => {
            startGame(numberOfLetters);
          };

          return (
            <WordLengthPickerButton key={numberOfLetters} onClick={onClick} style={{ backgroundColor }}>
              {numberOfLetters}
            </WordLengthPickerButton>
          );
        })}
      </WordLengthPickerWrapper>
      <KeyboardHint style={{ marginTop: 8 }}>Or press 3 to 8</KeyboardHint>
    </>
  );
};
